import { useMemo } from "react";

import {
  useDashboardSurfaces,
  type ResolvedDashboardRef,
} from "#app/components/dashboards/use-dashboard-surfaces";
import { useRecentDashboards } from "#app/components/dashboards/use-recent-dashboards";
import { useRecentItems, type RecentItem } from "#app/lib/hooks/use-recent-items";

/**
 * One row of the Recent card, newest first. A project rides its stored
 * snapshot (projects live in no workspace-wide map); a dashboard is the
 * resolved ref the Dashboards module's own Recent section renders.
 */
export type RecentListRow =
  | { key: string; kind: "issue"; visitedAt: number; item: RecentItem }
  | {
      key: string;
      kind: "dashboard";
      visitedAt: number;
      dashboard: ResolvedDashboardRef;
    };

/**
 * The data layer for the `recent-list` card: the viewer's recently-opened
 * Projects and dashboards, merged from the two recents stores — the
 * workspace-scoped entity recents (`useRecentItems`) and the dashboard
 * recents (`useRecentDashboards`) — sorted by last open and capped at
 * `limit`. A dashboard's open time is the server's `viewerLastViewedAt`, so a
 * dashboard row joins the list once the dashboard views have loaded.
 */
export function useRecentList(limit: number): RecentListRow[] {
  const { items } = useRecentItems();
  const { recents: recentDashboards } = useRecentDashboards();
  const { surfaceByRefKey } = useDashboardSurfaces();

  return useMemo(() => {
    const rows: RecentListRow[] = [];
    for (const item of items) {
      rows.push({
        key: `${item.kind}:${item.id}`,
        kind: "issue",
        visitedAt: item.visitedAt,
        item,
      });
    }
    for (const dashboard of recentDashboards) {
      const viewedAt = surfaceByRefKey.get(dashboard.refKey)?.view.viewerLastViewedAt;
      if (!viewedAt) continue;
      rows.push({
        key: `dashboard:${dashboard.refKey}`,
        kind: "dashboard",
        visitedAt: Date.parse(viewedAt),
        dashboard,
      });
    }
    rows.sort((a, b) => b.visitedAt - a.visitedAt);
    return rows.slice(0, limit);
  }, [items, limit, recentDashboards, surfaceByRefKey]);
}
